import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A piece of navigational view state (active tab, selected record, section…)
 * backed by a URL query param — so it survives reload and the browser
 * Back/Forward + macOS back-swipe work. Generalises `useSelectedWorkItem`.
 *
 * Drop-in for `useState<T>`: returns `[value, setValue]`.
 *
 * - When `value` equals `defaultValue` the param is dropped (clean URLs).
 * - `push` (default false → `replace`): set `true` for primary navigation
 *   (top-level tab, opening a master-detail record) so each change is a history
 *   entry the back-swipe can return to. Leave false for incidental/secondary
 *   updates (sub-filters, auto-selecting a default) to avoid history spam.
 * - `serialize`/`deserialize` for non-string state (e.g. an object); default is
 *   identity string round-trip.
 */
export function useUrlState<T = string>(
  param: string,
  defaultValue: T,
  opts: {
    push?: boolean;
    serialize?: (value: T) => string;
    deserialize?: (raw: string) => T;
  } = {},
): [T, (value: T) => void] {
  const { push = false, serialize, deserialize } = opts;
  const [params, setParams] = useSearchParams();

  const raw = params.get(param);
  const value =
    raw === null ? defaultValue : deserialize ? deserialize(raw) : (raw as unknown as T);

  const setValue = useCallback(
    (next: T) => {
      const serialized = serialize ? serialize(next) : String(next);
      const isDefault =
        serialized === (serialize ? serialize(defaultValue) : String(defaultValue));
      setParams(
        (prev) => {
          const params_ = new URLSearchParams(prev);
          if (isDefault) params_.delete(param);
          else params_.set(param, serialized);
          return params_;
        },
        { replace: !push },
      );
    },
    // defaultValue/serialize are expected to be stable (literals / module fns).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams, param, push],
  );

  return [value, setValue];
}
